import { AsyncPipe, DatePipe } from '@angular/common'
import { Component, Input, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatIcon } from '@angular/material/icon'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { Router } from '@angular/router'
import {
  DataContextBuilder,
  ElderDataToolbarComponent,
  ElderSearchBoxComponent,
  ElderSearchContextDirective,
  ElderSearchInputDirective,
  ElderSearchPanelComponent,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderToolbarContentDirective,
  Filter,
  IDataContext,
  Sort,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { ReplaySubject } from 'rxjs'
import { FilterBuilder } from '../../../orders/filter-builder'
import { WorkStationsIndicatorComponent } from '../../workstations/work-stations-indicator/work-stations-indicator.component'
import { WorkCenter } from '../work-center'
import { WorkCenterService } from '../work-center.service'
import { MatSlideToggle } from '@angular/material/slide-toggle'

@Component({
  selector: 'app-work-center-browser',
  templateUrl: './work-center-browser.component.html',
  styleUrls: ['./work-center-browser.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    AsyncPipe,
    DatePipe,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIcon,
    MatSortHeader,
    MatSlideToggle,
    ElderTableComponent,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    ElderToolbarContentDirective,
    ElderSearchBoxComponent,
    ElderSearchContextDirective,
    ElderSearchPanelComponent,
    ElderTranslatePipe,
    ElderSearchInputDirective,
    WorkStationsIndicatorComponent,
  ],
})
export class WorkCenterBrowserComponent implements OnDestroy {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  public readonly data: IDataContext<WorkCenter>
  public readonly forcedFilters$ = new ReplaySubject<Filter[]>(1)
  private _canAdd: boolean = false
  private router = inject(Router)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const workCenterService = inject(WorkCenterService)
    this.data = DataContextBuilder.start<WorkCenter>()
      .pageSize(50)
      .reloadOnLocalChanges()
      .autoStartOnFiltersAll([FilterBuilder.FACILITY_KEY], [new Sort('number', 'asc')])
      .build(workCenterService)
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnDestroy(): void {
    if (this.data) {
      this.data.close()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input({ required: true })
  public set facility(facilityId: string) {
    if (facilityId) {
      const facilityFilter = FilterBuilder.facility(facilityId)
      this.forcedFilters$.next([facilityFilter])
    }
  }

  @Input()
  public set canAdd(allowNew: boolean) {
    this._canAdd = allowNew
  }

  get canAdd(): boolean {
    return this._canAdd
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public openSideDetail(workCenter: WorkCenter): void {
    this.router.navigate([{ outlets: { side: ['shop-floor', 'work-centers', workCenter.id] } }])
  }

  public openDetail(workCenter: WorkCenter): void {
    if (workCenter) {
      this.router.navigate(['app', 'shop-floor', 'work-centers', workCenter.id])
    }
  }

  public create(): void {
    this.router.navigate([{ outlets: { side: ['shop-floor', 'work-centers', 'new'] } }])
  }
}
